// src/components/workAccountability/TaskAccountabilitySection.tsx
//
// Part 5: the Work Accountability section on a task — accumulated time, estimate,
// variance, policy summary, and the resolver's "why" — plus the time-entry UI
// (Part 6) and completion history (Part 8). All data comes from the backend.

import { useGetTaskTimeQuery, useGetEffectivePolicyQuery } from '../../api/accountabilityApi';
import type { RuleFieldKey } from '../../types/workAccountability';
import { formatMinutes, requirementsSummary, variancePct } from './waFormat';
import TaskTimeEntries from './TaskTimeEntries';
import CompletionHistory from './CompletionHistory';

interface Props {
  task: {
    id: string;
    workspaceId: string;
    assigneeUserId: string | null;
    estimatedMinutes: number | null;
    projectId?: string | null;
  };
  nameForUser?: (id: string) => string;
}

const KEY_WHY_FIELDS: RuleFieldKey[] = ['requireTimeBeforeCompletion', 'completionNote', 'proofOfDone', 'variance.explanationRequired'];

export default function TaskAccountabilitySection({ task, nameForUser }: Props) {
  const { data: time, isLoading: timeLoading } = useGetTaskTimeQuery(task.id, { skip: !task.id });
  const { data: effective, isError, error } = useGetEffectivePolicyQuery(
    { userId: task.assigneeUserId ?? undefined, workItemId: task.id, assigneeUserId: task.assigneeUserId ?? undefined, projectId: task.projectId ?? undefined },
    { skip: !task.id },
  );

  // Backend feature disabled (503) even though the frontend flag is on (out of
  // sync) — render nothing (fail closed).
  if (isError && (error as { status?: number })?.status === 503) return null;

  const actual = time?.accumulatedActualMinutes ?? 0;
  const v = variancePct(actual, task.estimatedMinutes);
  const rules = effective?.effective;

  return (
    <div className="space-y-4" data-testid="wa-task-section">
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Accumulated" value={timeLoading ? '…' : formatMinutes(actual)} />
        <Stat label="Estimated" value={formatMinutes(task.estimatedMinutes)} />
        <Stat label="Variance" value={timeLoading || v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`} tone={!timeLoading && v != null && v > 0 ? 'warn' : 'default'} />
      </div>

      {rules && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">Policy Summary</div>
          <div className="text-sm text-gray-800">{requirementsSummary(rules).join(' · ')}</div>
          {effective && effective.sourcePolicies.length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {KEY_WHY_FIELDS.map((f) => {
                const exp = effective.explanations[f];
                if (!exp || !exp.reason) return null;
                return (
                  <li key={f} className="text-xs text-gray-500">
                    {exp.reason}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      <div>
        <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">Time</div>
        <TaskTimeEntries taskId={task.id} editReasonRequired={rules?.editReasonRequired} />
      </div>

      <CompletionHistory taskId={task.id} nameForUser={nameForUser} />
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: 'default' | 'warn' }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-2 text-center">
      <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`text-base font-semibold ${tone === 'warn' ? 'text-amber-700' : 'text-gray-900'}`}>{value}</div>
    </div>
  );
}
